import React from 'react';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import DateRange from 'components/Form/DateRange';
import Textarea from 'components/Form/Textarea';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import { SubstanceAlcoholRecievedCounselingItemModel } from 'forms/sections/substance/alcohol/additional/types';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonRequired, CommonScrollIntoView
} from 'types/props';
import Comments from 'components/Form/Comments';

interface Props
  extends ValidationElementProps,
  Partial<SubstanceAlcoholRecievedCounselingItemModel> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  attribute?: string
}

export default class ReceivedCounseling extends ValidationElement<Props> {
  update = (updateValues: Partial<SubstanceAlcoholRecievedCounselingItemModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        CounselingDates: this.props.CounselingDates,
        TreatmentProviderName: this.props.TreatmentProviderName,
        TreatmentProviderAddress: this.props.TreatmentProviderAddress,
        AgencyName: this.props.AgencyName,
        AgencyAddress: this.props.AgencyAddress,
        UseSameAddress: this.props.UseSameAddress,
        CompletedTreatment: this.props.CompletedTreatment,
        NoCompletedTreatmentExplanation: this.props.NoCompletedTreatmentExplanation,
        Comments: this.props.Comments,
        ...updateValues,
      });
    }
  };

  updateCounselingDates = (
    values: SubstanceAlcoholRecievedCounselingItemModel['CounselingDates']
  ) => {
    this.update({ CounselingDates: values });
  };

  updateTreatmentProviderName = (
    values: SubstanceAlcoholRecievedCounselingItemModel['TreatmentProviderName']
  ) => {
    this.update({ TreatmentProviderName: values });
  };

  updateTreatmentProviderAddress = (
    values: SubstanceAlcoholRecievedCounselingItemModel['TreatmentProviderAddress']
  ) => {
    this.update({ TreatmentProviderAddress: values });
  };

  updateCompletedTreatment = (
    values: SubstanceAlcoholRecievedCounselingItemModel['CompletedTreatment']
  ) => {
    this.update({ CompletedTreatment: values });
  };

  updateNoCompletedTreatmentExplanation = (
    values: SubstanceAlcoholRecievedCounselingItemModel['NoCompletedTreatmentExplanation']
  ) => {
    this.update({ NoCompletedTreatmentExplanation: values });
  };

  updateAgencyName = (values: SubstanceAlcoholRecievedCounselingItemModel['AgencyName']) => {
    this.update({ AgencyName: values });
  };

  updateAgencyAddress = (values: SubstanceAlcoholRecievedCounselingItemModel['AgencyAddress']) => {
    this.update({ AgencyAddress: values });
  };

  updateUseSameAddress = (
    values: SubstanceAlcoholRecievedCounselingItemModel['UseSameAddress']
  ) => {
    this.update({ UseSameAddress: values });
  };

  updateComments = (values: SubstanceAlcoholRecievedCounselingItemModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    return (
      <div className="received-counseling">
        <Field
          attribute={`${attribute}.TreatmentProviderName`}
          title={i18n.t('substance.alcohol.receivedCounseling.heading.treatmentProviderName')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            attribute={`${attribute}.TreatmentProviderName`}
            name="TreatmentProviderName"
            className="treatment-provider-name"
            {...this.props.TreatmentProviderName}
            onUpdate={this.updateTreatmentProviderName}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          attribute={`${attribute}.TreatmentProviderAddress`}
          title={i18n.t('substance.alcohol.receivedCounseling.heading.treatmentProviderAddress')}
          optional
          help="substance.alcohol.receivedCounseling.help.treatmentProviderAddress"
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Location
            attribute={`${attribute}.TreatmentProviderAddress`}
            name="TreatmentProviderAddress"
            className="provider-address"
            {...this.props.TreatmentProviderAddress}
            layout={Location.ADDRESS}
            geocode
            showPostOffice
            onUpdate={this.updateTreatmentProviderAddress}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.AgencyName`}
          title={i18n.t('substance.alcohol.receivedCounseling.heading.agencyName')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            attribute={`${attribute}.AgencyName`}
            name="AgencyName"
            className="agency-name"
            {...this.props.AgencyName}
            onUpdate={this.updateAgencyName}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Branch
          attribute={`${attribute}.UseSameAddress`}
          name="UseSameAddress"
          label={i18n.t('substance.alcohol.receivedCounseling.heading.agencyAddress')}
          labelSize="h4"
          help="substance.alcohol.receivedCounseling.help.agencyAddress"
          className={`use-same-address ${this.props.UseSameAddress === 'No' ? 'no-margin-bottom' : ''}`.trim()}
          yesLabel="Same as above"
          noLabel="Different address"
          {...this.props.UseSameAddress}
          onUpdate={this.updateUseSameAddress}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
        />

        <Show when={this.props.UseSameAddress.value === 'No'}>
          <Field
            optional
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.AgencyAddress`}
          >
            <Location
              attribute={`${attribute}.AgencyAddress`}
              name="AgencyAddress"
              className="agency-address"
              {...this.props.AgencyAddress}
              layout={Location.ADDRESS}
              geocode
              showPostOffice
              onUpdate={this.updateAgencyAddress}
              required={this.props.required}
              onError={this.props.onError}
            />
          </Field>
        </Show>

        <Field
          attribute={`${attribute}.CounselingDates`}
          title={i18n.t('substance.alcohol.receivedCounseling.heading.counselingDates')}
          help="substance.alcohol.receivedCounseling.help.counselingDates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            attribute={`${attribute}.CounselingDates`}
            name="CounselingDates"
            className="counseling-dates"
            minDateEqualTo
            {...this.props.CounselingDates}
            onUpdate={this.updateCounselingDates}
            onError={this.props.onError}
            required={this.props.required}
            allowPresent={false}
            hideFromDay
            hideToDay
          />
        </Field>

        <Branch
          attribute={`${attribute}.CompletedTreatment`}
          name="CompletedTreatment"
          label={i18n.t('substance.alcohol.receivedCounseling.heading.completedTreatment')}
          labelSize="h4"
          className="completed-treatment"
          {...this.props.CompletedTreatment}
          onUpdate={this.updateCompletedTreatment}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
        />

        <Show when={['Yes', 'No'].includes(this.props.CompletedTreatment.value)}>
          <Field
            attribute={`${attribute}.NoCompletedTreatmentExplanation`}
            title={i18n.t('substance.alcohol.receivedCounseling.heading.noCompletedTreatment')}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              attribute={`${attribute}.NoCompletedTreatmentExplanation`}
              name="NoCompletedTreatmentExplanation"
              className="no-completed-treatment"
              {...this.props.NoCompletedTreatmentExplanation}
              onUpdate={this.updateNoCompletedTreatmentExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>
        <Comments
          title={i18n.t('substance.comments.optional')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
      </div>
    );
  }
}

ReceivedCounseling.defaultProps = {
  UseSameAddress: {},
  CompletedTreatment: {},
  Comments: {},
  onError: (value, arr) => arr,
};
